import { Check } from '@mui/icons-material';
import {
  Box,
  Button,
  Divider,
  List,
  ListItemButton,
  ListItemText,
  MenuItem,
  Popover,
  TextField,
  Typography
} from '@mui/material';
import { AxiosResponse } from 'axios';
import { useEffect, useState } from 'react';
import { PartnerCompanyEntity } from 'src/entities/PartnerCompanyEntity';
import { useMutationPartnerCompanies } from 'src/hooks/usePartnerCompanies.request';

type PartnerCompanySelectorProps = {
  selectedCompanyId: number | null;
  selectedCompanyName: string | null;
  partners: PartnerCompanyEntity[];
  onConfirm: (companyId: number, companyName: string) => void;
  buttonLabel?: string;
};

function PartnerCompanySelector({
  selectedCompanyId,
  selectedCompanyName,
  partners,
  onConfirm,
  buttonLabel = '確定企業を選択'
}: PartnerCompanySelectorProps) {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [tempSelectedId, setTempSelectedId] = useState<number>(selectedCompanyId);
  const { addPartnerCompany } = useMutationPartnerCompanies();

  const handleOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
    setDialogOpen(true);
  };

  const handleClose = () => {
    setAnchorEl(null);
    setDialogOpen(false);
  };

  useEffect(() => {
    setTempSelectedId(selectedCompanyId);
  }, [selectedCompanyId]);

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(event.target.value);
  };

  const handleCompanyClick = (partnerId: number) => {
    setTempSelectedId(partnerId);
  };

  const handleConfirm = () => {
    if (!tempSelectedId) {
      // eslint-disable-next-line no-alert
      alert('選択してください。');
      return;
    }

    const partner = partners?.find((it) => it.id === tempSelectedId);
    onConfirm(tempSelectedId, partner.name);
    setDialogOpen(false);
  };

  const filteredCompanies = partners?.filter((it) => it.name.toLowerCase().includes(searchQuery.toLowerCase()));

  const handleCreatePartner = async () => {
    if (!searchQuery) return;
    await addPartnerCompany.mutateAsync({
      name: searchQuery,
    }, {
      onSuccess: (response: AxiosResponse<PartnerCompanyEntity>) => {
        setTempSelectedId(response.data.id);
      }
    });
  };

  return (
    <>
      <Button
        variant={selectedCompanyId ? 'outlined' : 'contained'}
        size="small"
        onClick={handleOpen}
      >
        {buttonLabel}
      </Button>
      {buttonLabel === '確定企業を選択' && (
        <Typography variant="body2">
          {selectedCompanyName || ''}
        </Typography>
      )}

      <Popover
        open={dialogOpen}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
      >
        <Box sx={{ width: 400, p: 2 }}>
          <TextField
            fullWidth
            variant="outlined"
            size="small"
            placeholder="検索"
            value={searchQuery}
            onChange={handleSearchChange}
            sx={{ mb: 1, }}
          />
          <List sx={{ p: 0, m: 0 }}>
            {filteredCompanies?.map((partner) => (
              <ListItemButton
                key={['fixedCompany', partner.id].join('-')}
                onClick={() => handleCompanyClick(partner.id)}
                selected={tempSelectedId === partner.id}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                }}
              >
                <Check
                  sx={{
                    visibility: tempSelectedId === partner.id ? 'visible' : 'hidden',
                    color: 'primary.main',
                    mr: 1,
                    p: 0,
                    m: 0,
                  }}
                />
                <ListItemText
                  primary={partner.name}
                  primaryTypographyProps={{ fontSize: 14, color: 'black' }}
                />
              </ListItemButton>
            ))}
            {searchQuery && (
              <>
                <Divider sx={{ my: 1 }} />
                {/* eslint-disable-next-line @typescript-eslint/no-misused-promises */}
                <MenuItem onClick={handleCreatePartner}>
                  <ListItemText primary={`"${searchQuery}" を新規作成`} />
                </MenuItem>
              </>
            )}
          </List>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2, gap: 1 }}>
            <Button onClick={handleClose}>
              キャンセル
            </Button>
            <Button
              onClick={handleConfirm}
              variant="contained"
            >
              確定
            </Button>
          </Box>
        </Box>
      </Popover>
    </>
  );
}
export default PartnerCompanySelector;
